import { BaseCard, useCardActions } from '@/components/ui/base-card'
import { Button } from '@/components/ui/button'
import { CardContent, Card as ShadcnCard } from '@/components/ui/card'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useDeleteGrid } from '@/queries/grids/useDeleteGrid'
import { selectedGridIdSelector } from '@/redux/store/slices/map/mapSlice'
import { formatCurrency } from '@/utils'
import { Avatar, Paper, styled } from '@mui/material'
import { useAppSelector } from '@redux/hooks/useAppState'
import { MapPin, MoreHorizontal, Trash2 } from 'lucide-react'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog'
import { TargetingAreasMetricRow } from '../TargetingAreasMetricRow'
import { TargetingCoverageMetricRow } from '../TargetingCoverageMetricRow'

import type { Campaign, Grid as GridType, WorkspaceLocation } from 'honeygrid-types'
import type { FC } from 'react'

/** Made a custom card component
 * as material UI adds a margin bottom on the last child
 * This is simply a wrapper that takes in the size of its children
 * 
 * We can add styles by using the sx props - same as material ui
 * 
 * @usage
 * <Card
      onClick={handlePreviewJob}
      sx={{
        borderRadius: '10px',
        padding: '16px',
        cursor: 'pointer',
        background: snapshot.isDragging ? '#F2F2F2' : '#FFF',
      }}
    >
 */

interface StaffCardProps {
	avatar?: string
	/**
	 * Image URL for the avatar
	 */

	name: string
}

interface StatsCardProps {
	metric: string
	stat: string
}

interface CampaignCardProps {
	campaign: Campaign
	onChangeStatus?: () => void
}

interface GridCardProps {
	grid: GridType
	isSelected?: boolean
}

interface LocationCardProps {
	location: WorkspaceLocation
	isSelected?: boolean
	onRemove?: (locationId: WorkspaceLocation['uuid']) => void
}

const Card = styled(Paper)(({ theme }) => ({
	...theme.typography.body2,
	textAlign: 'left',
	fontWeight: 'bold',
	borderRadius: '8px',
}))

const StaffCard: FC<StaffCardProps> = ({ avatar, name }) => {
	return (
		<div className="min-w-[120px] mr-2" data-testid="card-component">
			<ShadcnCard>
				<CardContent className="p-4">
					<div className="flex items-center gap-4">
						<Avatar className="h-10 w-10">
							{!avatar && <span className="uppercase">{name.charAt(0)}</span>}
							{avatar && <img src={avatar} alt={name} className="aspect-square" />}
						</Avatar>
						<span className="capitalize font-medium" data-testid="name-component">
							{name}
						</span>
					</div>
				</CardContent>
			</ShadcnCard>
		</div>
	)
}

const StatsCard: FC<StatsCardProps> = ({ metric, stat }) => {
	return (
		<div className="space-y-2">
			<h3 className="text-xl font-semibold" data-testid="metric-text">
				{metric}
			</h3>
			<ShadcnCard>
				<CardContent className="p-4">
					<p
						className={cn(
							'text-5xl font-semibold text-center',
							metric === 'Total Conversions' && 'text-green-600'
						)}
						data-testid="stat-text"
					>
						{stat}
					</p>
				</CardContent>
			</ShadcnCard>
		</div>
	)
}

/****
 * 
 * This is a campaign card
 * 
 * 
 * Needs:
 * 
 * const [live, setLive] = useState(true);

    const changeStatus = () => {
        setLive(isLive => !isLive);
    };

 * @usage  
 * <CampaignCard
    name="test campaign"
    budget={100}
    live={live}
    leads={37}
    conversions={2}
    onChangeStatus={changeStatus}
    />
 */

const CampaignCard: FC<CampaignCardProps> = ({ campaign, onChangeStatus }) => {
	const { name, budget, status } = campaign
	return (
		<ShadcnCard data-testid="campaign-card" className="m-1">
			<CardContent className="p-4 space-y-3">
				<h3 className="text-sm font-semibold">{name}</h3>
				<p className="text-sm text-muted-foreground">{formatCurrency(budget?.amount)}</p>
				<select
					onChange={onChangeStatus}
					value={status}
					className={cn(
						'h-8 w-24 rounded-full text-xs px-3',
						status === 'active' ? 'bg-green-700 text-white' : 'bg-muted text-muted-foreground'
					)}
				>
					<option value="active">Live</option>
					<option value="paused">Paused</option>
					<option value="cancelled">Cancelled</option>
					<option value="archived">Archived</option>
				</select>
			</CardContent>
		</ShadcnCard>
	)
}

const GridCard: FC<GridCardProps> = ({ grid, isSelected }) => {
	const navigate = useNavigate()
	const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
	const { workspaceId = '' } = useParams()
	const selectedGridId = useAppSelector(selectedGridIdSelector)
	const { mutate: deleteGrid, isPending: isDeleting } = useDeleteGrid()
	const { createEditAction, createRemoveAction } = useCardActions()

	const handleRemove = async () => {
		deleteGrid(grid.uuid, {
			onSuccess: () => {
				enqueueSnackbar(`Removed ${grid.name}`, { variant: 'success' })
				setDeleteConfirmationOpen(false)
			},
			onError: () => {
				enqueueSnackbar(`Failed to remove ${grid.name}`, { variant: 'error' })
			},
		})
	}

	const handleCardClick = () => {
		if (grid.uuid === selectedGridId) {
			navigate(`/${workspaceId}/grids`, { replace: true })
		} else {
			navigate(`/${workspaceId}/grids?gridId=${grid.uuid}`, {
				replace: true,
			})
		}
	}

	// Create actions for the card
	const actions = [
		createEditAction(() => navigate(`${grid.uuid}`)),
		createRemoveAction(() => setDeleteConfirmationOpen(true)),
	]

	return (
		<>
			<BaseCard
				title={grid.name}
				isSelected={isSelected}
				isClickable={true}
				onClick={handleCardClick}
				actions={actions}
				showActionsOnHover={false} // Always show actions for grids
				className="w-full"
				data-testid="grid-card-container"
			>
				<div className="space-y-1 text-muted-foreground">
					<TargetingAreasMetricRow grid_version={grid.currentVersion} />
					<TargetingCoverageMetricRow grid_version={grid.currentVersion} />
				</div>
			</BaseCard>
			<DeleteConfirmationDialog
				isOpen={deleteConfirmationOpen}
				title={`${grid.name}`}
				message={`Are you sure you want to remove ${grid.name}? Any active campaigns using this grid will no longer be able to update their targeting.`}
				onRemove={handleRemove}
				onCancel={() => setDeleteConfirmationOpen(false)}
				isRemoving={isDeleting}
			/>
		</>
	)
}

const LocationCard: FC<LocationCardProps> = ({ location, isSelected, onRemove }) => {
	const navigate = useNavigate()
	const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
	const { workspaceId = '' } = useParams()

	const handleCardClick = () => {
		navigate(`/${workspaceId}/locations/${location.place_id}`)
	}

	const handleRemove = async () => {
		onRemove?.(location.uuid)
		setDeleteConfirmationOpen(false)
	}

	return (
		<>
			<ShadcnCard
				className={cn(
					'w-full transition-colors hover:border-primary cursor-pointer',
					isSelected && 'border-2 border-primary'
				)}
				onClick={handleCardClick}
			>
				<CardContent>
					<div className="flex items-center gap-2">
						<MapPin className="h-5 w-5 text-primary flex-shrink-0" />
						<div className="flex-1 min-w-0">
							<h3 className="text-base font-medium truncate">{location.name}</h3>
							<p className="text-sm text-muted-foreground truncate">{location.address}</p>
						</div>
						<DropdownMenu>
							<DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
								<Button variant="ghost" size="icon" className="h-8 w-8">
									<MoreHorizontal className="h-4 w-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem
									className="text-destructive focus:text-destructive"
									onClick={(e) => {
										e.stopPropagation()
										setDeleteConfirmationOpen(true)
									}}
								>
									<Trash2 className="mr-2 h-4 w-4" />
									<span>Remove</span>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</CardContent>
			</ShadcnCard>
			<DeleteConfirmationDialog
				isOpen={deleteConfirmationOpen}
				title={location.name}
				message={`Are you sure you want to remove ${location.name}?`}
				onRemove={handleRemove}
				onCancel={() => setDeleteConfirmationOpen(false)}
			/>
		</>
	)
}

export { Card, StaffCard, StatsCard, CampaignCard, GridCard, LocationCard }
export type { StaffCardProps, StatsCardProps, CampaignCardProps, GridCardProps, LocationCardProps }
